import type {
  IndividualMetric,
  MetricDetailsResponse,
} from '@dashhudson/api-client/dashboard/model/raw';

import { defineStore } from 'pinia';
import { ref } from 'vue';

import DashboardAPI from '@/apis/dashboards';
import { MULTI_CHANNEL_CONTENT_KEY, MULTI_CHANNEL_KEY } from '@/models/dashboards/channels.enum';
import { useMetricDetailsQuery } from '@/shared/queries/metric-details';
import { logger } from '@/utils/logger';

import { useFlagStore } from './flag';

// Channel and report-type keys arrive as runtime strings, so lookups go through a loose view.
type MetricsByReportType = Record<string, Record<string, IndividualMetric> | undefined>;
type MetricDetailsByChannel = Record<string, MetricsByReportType | undefined>;

// TODO: Move metric types and comparable metrics into this store
export const useMetricsStore = defineStore('metrics', () => {
  // Writable refs mirrored from the query so consumers (and tests) stay unchanged.
  const metricDetails = ref<MetricDetailsResponse>({});
  const metricDetailsReady = ref(false);
  const metricDetailsError = ref(false);

  const legacyMetricDetails = ref<MetricDetailsResponse>({});
  const legacyMetricDetailsReady = ref(false);
  const legacyMetricDetailsError = ref(false);

  async function fetchMetricDetails() {
    const query = useMetricDetailsQuery();
    // refresh() dedupes and only refetches when stale.
    await query.refresh();
    if (query.data.value) {
      metricDetails.value = query.data.value;
      metricDetailsReady.value = true;
      metricDetailsError.value = false;
    } else if (query.error.value) {
      metricDetailsError.value = true;
      logger.error('Failed to fetch metric details', {}, query.error.value);
    }
  }

  function channelMetrics(channel: string | undefined, metricReportTypeKey: string | undefined) {
    if (!channel || !metricReportTypeKey) return undefined;
    return (metricDetails.value as MetricDetailsByChannel)?.[channel]?.[metricReportTypeKey];
  }

  function getMetricKeysByParent(
    channel: string,
    metricReportTypeKey: string,
    parentMetric: string,
    includeHidden = false,
  ) {
    const flagStore = useFlagStore();
    const allChannelTypeMetrics = channelMetrics(channel, metricReportTypeKey) ?? {};
    return Object.keys(allChannelTypeMetrics).filter((metricKey) => {
      const {
        parent_metric: currentParentMetric,
        hide_on_dashboards: hideOnDashboards,
        required_feature_flag: requiredFeatureFlag,
      } = allChannelTypeMetrics[metricKey];
      const parentMetricMatch = metricKey === parentMetric || currentParentMetric === parentMetric;
      const hasRequiredFlag = Boolean(
        !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
      );
      return (includeHidden || !hideOnDashboards) && parentMetricMatch && hasRequiredFlag;
    });
  }

  function getTargetMetricDetails(
    channel: string | undefined,
    metricReportTypeKey: string | undefined,
    metric: string | undefined,
  ): IndividualMetric | undefined {
    if (!metric) return undefined;
    return channelMetrics(channel, metricReportTypeKey)?.[metric];
  }

  function getTargetMetricDetailsMultiChannel(
    metric: string | undefined,
    isContentReport = false,
  ): IndividualMetric | undefined {
    if (!metric) return undefined;
    const multiChannel = metricDetails.value as Record<string, Record<string, IndividualMetric>>;
    if (isContentReport) return multiChannel?.[MULTI_CHANNEL_CONTENT_KEY]?.[metric];
    return multiChannel?.[MULTI_CHANNEL_KEY]?.[metric];
  }

  /**
   * NOTE: This hopefully will be a temporary solution until we can move custom metrics to the new
   * metric details format. Do not propagate this pattern if possible.
   */
  async function initializeLegacyMetricDetails() {
    if (!legacyMetricDetails.value) return;
    legacyMetricDetailsError.value = false;
    try {
      const response = await DashboardAPI.getMetricDetails({ legacyCopy: true });
      legacyMetricDetails.value = response?.data;
      legacyMetricDetailsReady.value = true;
    } catch (err) {
      legacyMetricDetailsError.value = true;
      logger.error('Failed to fetch legacy metric details', {}, err);
    }
  }

  return {
    metricDetailsReady,
    metricDetailsError,
    metricDetails,
    fetchMetricDetails,
    getMetricKeysByParent,
    getTargetMetricDetails,
    getTargetMetricDetailsMultiChannel,
    initializeLegacyMetricDetails,
    legacyMetricDetails,
    legacyMetricDetailsReady,
    legacyMetricDetailsError,
  };
});
